/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react'

import styled from '@emotion/styled'

import { UiIcon } from 'modules/ui/atoms/Icon'
import { DEFAULT_THEME_COLORS } from 'css/theme'

export interface ToggleSwitchProps extends React.ComponentPropsWithRef<'input'> {
  /** Persists "checked" state of component, defaults to false */
  checked?: boolean
  /** Disables interactivity with component */
  disabled?: boolean
  /** Component size */
  displaySize?: 'sm' | 'md'
  /** Function to be called when component's "checked" status is changed */
  onChange: (...event: any[]) => void
  ariaLabelledBy?: string
  dataId?: string
}

const Row = styled.div({
  display: 'flex',
  alignItems: 'center',
  minHeight: '44px',
})

type ToggleSwitchContainerProps = {
  disabled?: boolean
  checked?: boolean
  displaySize?: 'sm' | 'md'
}

const ToggleSwitchContainer = styled.div<ToggleSwitchContainerProps>(
  {
    flexBasis: 'auto',
    display: 'flex',
    alignItems: 'center',

    padding: '2px 0',

    '&:focus-within': {
      borderRadius: '18px',
      outline: '2px #326CDB dotted',
      outlineOffset: '2px',
    },

    cursor: 'inherit',
    position: 'relative',

    '.checkMark': {
      position: 'absolute',
      display: 'none',
    },
  },
  ({ disabled, checked, displaySize }) => {
    const styles: any = {
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? '25%' : 1,

      width: displaySize === 'sm' ? '28px' : '36px',
      height: displaySize === 'sm' ? '16px' : '20px',
    }

    if (checked) {
      styles['.checkMark'] = {
        display: 'inline',

        top: displaySize === 'sm' ? '-1px' : '1px',
        left: displaySize === 'sm' ? '0px' : '2px',
      }
    }

    return styles
  },
)

const ToggleInput = styled.input({
  position: 'absolute',
  opacity: 0,
  left: 0,
  width: '10px',
  height: '10px',
})

const ToggleSlider = styled.span<ToggleSwitchContainerProps>(
  {
    position: 'relative',
    top: 0,
    left: 0,
    backgroundColor: DEFAULT_THEME_COLORS['--theme-color--neutral-95'],
    border: '1px solid',
    borderColor: '#847F7B',
    borderRadius: '18px',
    boxShadow:
      'inset 1px 2px 2px rgba(255, 255, 255, 0.08), inset 0px -2px 4px rgba(0, 0, 0, 0.24), inset 0px -1px 1px rgba(0, 0, 0, 0.24)',

    transition: 'background-color 0.3s',

    '&:hover': {
      boxShadow: '0px 0px 0px 2px rgba(0, 0, 0, 0.12)',
      cursor: 'pointer',
    },

    '&:before': {
      content: '""',
      position: 'absolute',
      left: '3px',
      backgroundColor: 'darkgray',
      borderRadius: '100%',
      transition: 'left 0.3s ease',
    },
  },
  ({ checked, displaySize }) => {
    const styles: any = {
      width: displaySize === 'sm' ? '28px' : '36px',
      height: displaySize === 'sm' ? '16px' : '20px',

      '&:before': {
        top: displaySize === 'sm' ? '3px' : '4px',
        width: displaySize === 'sm' ? '8px' : '10px',
        height: displaySize === 'sm' ? '8px' : '10px',
      },
    }

    if (checked) {
      const otherStyles: any = {
        backgroundColor: DEFAULT_THEME_COLORS['--theme-color--brand-primary'],
        borderColor: 'rgba(0, 0, 0, .24)',
        boxShadow:
          'inset 1px 2px 2px rgba(255, 255, 255, 0.12), inset 0px -2px 4px rgba(0, 0, 0, 0.24), inset 0px -1px 1px rgba(0, 0, 0, 0.24)',

        '&:hover': {
          boxShadow: '0px 0px 0px 2px ' + DEFAULT_THEME_COLORS['--theme-color--brand-lightest'],
          cursor: 'pointer',
        },

        '&:before': {
          left: displaySize === 'sm' ? '14px' : '19px',
          width: displaySize === 'sm' ? '10px' : '12px',
          height: displaySize === 'sm' ? '10px' : '12px',
          top: displaySize === 'sm' ? '2px' : '3px',

          backgroundColor: DEFAULT_THEME_COLORS['--theme-color--neutral-95'],
        },
      }

      // Returns the merged styles
      return Object.assign({}, styles, otherStyles)
    }

    return styles
  },
)

export const ToggleSwitch = (props: ToggleSwitchProps) => {
  const {
    checked = false,
    disabled,
    onChange,
    displaySize = 'md',
    ariaLabelledBy,
    dataId = '',
  } = props

  const handleChange = () => {
    if (disabled) {
      return
    }

    onChange()
  }

  const handleKeyDown = (e: any) => {
    if (disabled) {
      return
    }

    if (e.keyCode === 13 || e.keyCode === 32) {
      e.preventDefault()

      onChange()
    }
  }

  const checkColor = DEFAULT_THEME_COLORS['--theme-color--neutral-95']

  return (
    <Row>
      <ToggleSwitchContainer
        disabled={disabled}
        checked={checked}
        onClick={handleChange}
        displaySize={displaySize}
      >
        <ToggleInput
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          aria-checked={!!checked}
          aria-labelledby={ariaLabelledBy}
          role="switch"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={handleKeyDown}
          data-id={dataId}
        />
        <ToggleSlider checked={checked} displaySize={displaySize} />
        <span className="checkMark">
          <UiIcon name="big_check" color={checkColor} w={16} h={16} />
        </span>
      </ToggleSwitchContainer>
    </Row>
  )
}
